'use client';

import { focusRing } from './focus-ring';
import styles from './switch.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

interface SwitchProps {
  /** Current state. Controlled — the caller owns the value. */
  checked: boolean;
  onChange: (next: boolean) => void;
  /**
   * Accessible name. Required: a switch is an icon-only control, so without
   * this it announces as an unlabelled toggle.
   */
  label: string;
  disabled?: boolean;
  /** On-state colour. `sage` for nature-ish options (extra vegetables). */
  tone?: 'accent' | 'sage';
  className?: string;
  /** id of the element describing this switch (aria-describedby) — for
   *  switches whose reason-to-toggle lives in adjacent text, like the
   *  direction cards' description + payoff (#994). */
  describedBy?: string;
}

/**
 * Switch — an on/off toggle for a setting that applies immediately, as opposed
 * to `Checkbox`, which selects an item within a form or list. The distinction
 * matters for screen readers: this reports `role="switch"` with on/off, while a
 * checkbox reports checked/unchecked.
 *
 * The track is `--switch-w` x `--switch-h`, but the button carries a
 * `--touch-min` hit area via an inset pseudo-element so the visual can stay
 * compact in a dense row without dropping under the WCAG 2.5.5 floor.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  tone = 'accent',
  className,
  describedBy,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        styles.track,
        checked && styles.isOn,
        tone === 'sage' && styles.toneSage,
        focusRing,
        className,
      )}
    >
      <span className={styles.thumb} aria-hidden="true" />
    </button>
  );
}
